import type { ReactNode } from 'react';
import { useTranslation } from '../../providers/UIProvider';
import { readIssueMeta } from '../../utils';
import type { ValidationIssue } from '../../types';

/** Minimal shape needed to render an issue's message — accepts both a full
 * `ValidationIssue` and the synthetic `CombinedIssue` from `combineIssues`. */
export type ErrorEntryIssue = Pick<ValidationIssue, 'message'> & {
  meta?: ValidationIssue['meta'];
};

export interface ErrorEntryProps {
  issue: ErrorEntryIssue;
  /**
   * When `true`, the "expected" value is shown in the message when available.
   * This is useful for validation errors where the expected value
   * provides additional context.
   */
  showActual?: boolean;
}

function renderWithBold(text: string): ReactNode {
  const parts: ReactNode[] = [];
  const regex = /<b>(.*?)<\/b>/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(<strong key={i++}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}

/** Renders a single ValidationIssue. When `issue.meta.expected` is present
 * the message is formatted as `"<message> (expected: <expected>)"` via the
 * localized `components.errorEntry.withExpected` key; otherwise the raw
 * message is rendered. The expected/actual values inside the localized
 * template are wrapped in `<b>…</b>` markers and rendered as `<strong>`. */
export function ErrorEntry({ issue, showActual }: ErrorEntryProps) {
  const t = useTranslation('components.errorEntry');
  const expected = readIssueMeta(issue, 'expected');
  const actual = readIssueMeta(issue, 'actual');
  if (expected !== null) {
    if (showActual && actual !== null) {
      return renderWithBold(t('withActualExpected', { message: issue.message, expected, actual }));
    }
    return renderWithBold(t('withExpected', { message: issue.message, expected }));
  }
  return <>{issue.message}</>;
}
